/**
 * API Service
 *
 * Centralized HTTP client for all backend API calls.
 *
 * Why axios instead of fetch?
 * - Automatic JSON parsing
 * - Request/response interceptors (for adding auth headers globally)
 * - Better error handling
 * - Request cancellation support
 */

import axios, { AxiosError } from 'axios';
import type {
  User,
  UpdateProfileRequest,
  PaginatedResponse,
  ApiError,
  SystemMetrics,
  DockerOverview,
} from '../types';

// API base URL
// In development: Vite proxy forwards /api to http://localhost:4002
// In production: nginx serves both frontend and /api on same domain
const API_BASE_URL = import.meta.env.DEV ? '/api' : '/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests
});

/**
 * Request Interceptor
 *
 * Automatically adds JWT token to all requests if it exists.
 * This means we don't have to manually add Authorization header in every API call.
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 *
 * Handles common error cases globally.
 * If token expires (401), clear it and redirect to login.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear auth state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Authentication API
// =============================================================================

export const authApi = {
  /**
   * Get Google OAuth login URL
   */
  getGoogleAuthUrl: async (redirectTo?: string): Promise<string> => {
    const params = new URLSearchParams();
    if (redirectTo) {
      params.append('redirect_to', redirectTo);
    }
    return `${API_BASE_URL}/auth/google?${params.toString()}`;
  },

  /**
   * Get current user profile (requires authentication)
   */
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (updates: UpdateProfileRequest): Promise<User> => {
    const response = await apiClient.patch<User>('/auth/profile', updates);
    return response.data;
  },

  /**
   * Logout (clears session and audit trail)
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  },

  /**
   * Validate token (used by other services)
   */
  validateToken: async (): Promise<{ valid: boolean; user?: User }> => {
    try {
      const response = await apiClient.get<User>('/auth/validate');
      return { valid: true, user: response.data };
    } catch {
      return { valid: false };
    }
  },
};

// =============================================================================
// User Management API (Admin Only)
// =============================================================================

export const userApi = {
  /**
   * Get paginated list of users
   */
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
  }): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/users', { params });
    return response.data;
  },

  /**
   * Get specific user by ID
   */
  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  /**
   * Approve pending user
   */
  approveUser: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/approve`);
    return response.data;
  },

  /**
   * Disable user
   */
  disableUser: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/disable`);
    return response.data;
  },

  /**
   * Enable/reactivate disabled user
   */
  enableUser: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/enable`);
    return response.data;
  },

  /**
   * Promote user to admin
   */
  promoteUser: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/promote`);
    return response.data;
  },

  /**
   * Delete user (admin only, cannot delete yourself)
   */
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },
};

// =============================================================================
// System Metrics API (Admin Only — Server Health widget)
// =============================================================================

export const systemApi = {
  /**
   * Get a real-time snapshot of server health and resource usage.
   * Supports cancellation via an AbortSignal for polling cleanup.
   */
  getMetrics: async (signal?: AbortSignal): Promise<SystemMetrics> => {
    const response = await apiClient.get<SystemMetrics>('/system/metrics', { signal });
    return response.data;
  },

  /**
   * Get all Docker containers on the host grouped by Compose project, with live
   * CPU / memory / network stats. Supports cancellation for polling cleanup.
   */
  getContainers: async (signal?: AbortSignal): Promise<DockerOverview> => {
    const response = await apiClient.get<DockerOverview>('/system/containers', { signal });
    return response.data;
  },
};

/**
 * Helper function to extract error message from API error
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    return apiError?.message || error.message;
  }
  return error instanceof Error ? error.message : 'An unknown error occurred';
};
